if (!customElements.get('product-bar')) {
  class ProductBar extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector('button[name="add"]');
      this.footer = document.querySelector('.footer');
      this.debouncedResize = debounce(() => this.pad(), RESIZE_DEBOUNCE_TIMER);
      this.handleBackInStockModal = this.triggerBackInStockModal.bind(this);

      /** @type {QuantitySelect} */
      // @ts-ignore
      this.quantitySelector = this.querySelector(
        'ui-select[data-name="quantity"]',
      );
    }

    connectedCallback() {
      this.productForm = document.querySelector('product-form');
      this.configurePosition();
      this.createObserver();
      this.onQuanityChange = this.onQuantityChange.bind(this);
      this.quantitySelector.addEventListener('change', this.onQuantityChange);
    }

    disconnectedCallback() {
      this.quantitySelector.removeEventListener(
        'change',
        this.onQuantityChange,
      );
      this.destroy();
    }

    onQuantityChange(e) {
      if (e.isTrusted) {
        this.quantitySelector.onInputChange(e);
      }
    }

    configurePosition() {
      this.positionMQLs = [];
      this.positionMap = new WeakMap();
      const positions = this.getAttribute('position')?.split('|') ?? ['bottom'];

      for (const positionString of positions) {
        const [position, breakpoint = null] = positionString.split('@');
        this.position ??= position;

        if (!breakpoint) {
          this.defaultPosition = position;
          continue;
        }

        const queryString = breakpointQuery(breakpoint);
        const mql = window.matchMedia(queryString);
        const callback = this.onPositionBreakpoint.bind(this);
        mql.addEventListener('change', callback);
        this.positionMQLs.push(mql);
        this.positionMap.set(mql, { position, callback });
      }

      this.getInitialPosition();
    }

    getInitialPosition() {
      const mql = this.positionMQLs.find(mql => mql.matches);

      if (!mql) {
        this.setPosition(this.defaultPosition);
        return;
      }

      const { position } = this.positionMap.get(mql);
      this.setPosition(position);
    }

    onPositionBreakpoint(e) {
      const { matches, currentTarget } = e;
      const { position } = this.positionMap.get(currentTarget);

      if (matches) {
        this.setPosition(position);
      } else {
        const currentIndex = this.positionMQLs.indexOf(currentTarget);
        const fallbackMQLs = this.positionMQLs.slice(0, currentIndex);

        for (const mql of fallbackMQLs.toReversed()) {
          if (mql.matches) {
            const { position } = this.positionMap.get(mql);
            this.setPosition(position);
            return;
          }
        }

        this.setPosition(this.defaultPosition);
      }
    }

    setPosition(position) {
      this.position = position;
      this.pad();
    }

    createObserver() {
      this.intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (
            !entries[0].isIntersecting &&
            (entries[0].boundingClientRect.top < 0 || this.position !== 'top')
          ) {
            this.show();
          } else {
            this.hide();
          }
        },
        { threshold: 1 },
      );

      this.intersectionObserver.observe(this.productForm);

      if (this.getAttribute('fluid-height') !== null) {
        this.resizeObserver = new ResizeObserver(
          this.debouncedResize.bind(this),
        );
        this.resizeObserver.observe(this);
      }
    }

    observeVariantChange() {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        event => {
          const { variant } = event.data;

          this.toggleOutOfStock(variant.available);
        },
      );
    }

    triggerBackInStockModal(e) {
      if (!this.coreBackInStockTrigger) {
        console.debug('Sticky ATC: No "Back in Stock" trigger found, aborting');
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      this.coreBackInStockTrigger.click();
    }

    toggleOutOfStock(available = true) {
      this.button.removeAttribute('disabled');
      this.button.firstElementChild.textContent = available
        ? window.variantStrings.addToCart
        : this.coreBackInStockTrigger.textContent;

      if (!available) {
        this.button.addEventListener('click', this.handleBackInStockModal);
      } else {
        this.button.removeEventListener('click', this.handleBackInStockModal);
      }
    }

    destroy() {
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.coreMutationObserver?.disconnect();
      this.stockObserver?.disconnect();
      this.pad(true);

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }

      for (const mql of this.positionMQLs) {
        const { callback } = this.positionMap.get(mql);
        mql.removeEventListener('change', callback);
      }
    }

    pad(reset = false) {
      gsap.set(
        this.footer,
        this.position === 'bottom' && !reset
          ? { paddingBottom: this.offsetHeight }
          : { clearProps: 'paddingBottom' },
      );
    }

    hide() {
      document.body.classList.remove('has-product-bar');
      gsap.to(this, {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'sine.out',
      });
    }

    show() {
      document.body.classList.add('has-product-bar');

      gsap.set(this, {
        autoAlpha: 1,
        y: getComputedStyle(this).getPropertyValue('--hide-offset'),
        overwrite: true,
      });
      gsap.to(this, {
        y: 0,
        duration: 0.3,
        ease: 'power3.out',
      });
    }
  }

  customElements.define('product-bar', ProductBar);
}
